import { useEffect, useRef } from "react";
import { API_BASE } from "@/lib/apiClient";

const DEFAULT_POLL_MS = 60_000;
const RECONNECT_MS = 5_000;
const REFRESH_DEBOUNCE_MS = 750;

function eventsStreamUrl(): string {
  const path = "/api/v1/events";
  if (!API_BASE) return path;
  return `${API_BASE}${path}`;
}

/**
 * Live-Aktualisierung über SSE; bei Verbindungsfehlern periodisches Polling als Fallback.
 */
export function useBookingRealtimeRefresh(
  onRefresh: () => void,
  options?: { enabled?: boolean; pollIntervalMs?: number },
): void {
  const onRefreshRef = useRef(onRefresh);
  onRefreshRef.current = onRefresh;

  const enabled = options?.enabled ?? true;
  const pollIntervalMs = options?.pollIntervalMs ?? DEFAULT_POLL_MS;

  useEffect(() => {
    if (!enabled) return;

    let disposed = false;
    let eventSource: EventSource | null = null;
    let reconnectTimer: number | null = null;
    let pollTimer: number | null = null;
    let debounceTimer: number | null = null;

    const triggerRefresh = () => {
      if (debounceTimer != null) {
        window.clearTimeout(debounceTimer);
      }
      debounceTimer = window.setTimeout(() => {
        debounceTimer = null;
        onRefreshRef.current();
      }, REFRESH_DEBOUNCE_MS);
    };

    const scheduleReconnect = () => {
      if (disposed || reconnectTimer != null) return;
      reconnectTimer = window.setTimeout(() => {
        reconnectTimer = null;
        connect();
      }, RECONNECT_MS);
    };

    const startPolling = () => {
      if (disposed || pollTimer != null) return;
      pollTimer = window.setInterval(triggerRefresh, pollIntervalMs);
    };

    const stopPolling = () => {
      if (pollTimer != null) {
        window.clearInterval(pollTimer);
        pollTimer = null;
      }
    };

    const connect = () => {
      if (disposed) return;
      eventSource?.close();
      eventSource = new EventSource(eventsStreamUrl(), { withCredentials: true });

      eventSource.addEventListener("booking_changed", () => {
        triggerRefresh();
      });

      eventSource.onopen = () => {
        stopPolling();
      };

      eventSource.onerror = () => {
        eventSource?.close();
        eventSource = null;
        startPolling();
        scheduleReconnect();
      };
    };

    connect();
    startPolling();

    return () => {
      disposed = true;
      eventSource?.close();
      if (reconnectTimer != null) window.clearTimeout(reconnectTimer);
      if (pollTimer != null) window.clearInterval(pollTimer);
      if (debounceTimer != null) window.clearTimeout(debounceTimer);
    };
  }, [enabled, pollIntervalMs]);
}
